import { useEffect, useState, useRef } from "react";
import searchPnc from "../../assets/searchPnc.svg";
import folderPnc from "../../assets/folderPnc.svg";
import dropPnc from "../../assets/dropPnc.svg";
import fusoChat from "../../assets/fusoChat.svg";
import "./SearchParameters.css";
import { CHAT_API_ENDPOINTS } from "../../api/CHAT_API_ENDPOINTS";
import { useTranslation } from "react-i18next";
import { DEFAULT_DEBOUNCE_MS } from "../../shared/hooks/useDebouncedValue";
import SuggestionSkeleton from "../../shared/search/SuggestionSkeleton";
import { ErrorState } from "../../shared/search/SearchStates";

const SearchParameters = ({
  searchType,
  setSearchType,
  vinInput,
  handleVinInputChange,
  isLoadingVin,
  vinError,
  modelInput,
  classificationInput,
  handleModelInputChange,
  handleClassificationInputChange,
  isLoadingModelClass,
  modelClassError,
  isSearchValid,
  groups,
  subGroups,
  selectedGroup,
  selectedGroupMainGroup,
  selectedSubGroup,
  selectedSubGroupMainGroup,
  groupDropdownOpen,
  subGroupDropdownOpen,
  setGroupDropdownOpen,
  setSubGroupDropdownOpen,
  handleGroupSelect,
  handleSubGroupSelect,
  isLoadingSubGroups,
  resetSearchState,
  // key,
  cycleId,
  isNewCycle = false,
  onSelectionChange,
  onValidationChange,
  preserveValues = false,
}) => {
  const { t } = useTranslation();
  const instanceId = `${cycleId}-search-params`;

  // Modal states for mobile view
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [subGroupModalOpen, setSubGroupModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Publication input state
  const [publicationNumberInput, setPublicationNumberInput] = useState("");

  // Model autocomplete state
  const [modelSuggestions, setModelSuggestions] = useState([]);
  const [showModelSuggestions, setShowModelSuggestions] = useState(false);
  const [isLoadingModelSuggestions, setIsLoadingModelSuggestions] = useState(false);
  const modelInputRef = useRef(null);
  const modelSuggestionsRef = useRef(null);
  const modelSuggestionsRequestIdRef = useRef(0);
  const latestModelQueryRef = useRef("");
  const lastModelInputRef = useRef("");
  const lastModelDetailsKeyRef = useRef("");
  const lastVinInputRef = useRef("");
  // Tracks the active tab so an in-flight model-details fetch started on the
  // Model tab can detect a tab switch that happened before it resolved —
  // closures over `searchType` would otherwise still see the old tab.
  const searchTypeRef = useRef(searchType);
  useEffect(() => {
    searchTypeRef.current = searchType;
  }, [searchType]);

  // VIN autocomplete state
  const [vinSuggestions, setVinSuggestions] = useState([]);
  const [showVinSuggestions, setShowVinSuggestions] = useState(false);
  const [isLoadingVinSuggestions, setIsLoadingVinSuggestions] = useState(false);
  const vinInputRef = useRef(null);
  const vinSuggestionsRef = useRef(null);
  const [noVinResultsFound, setNoVinResultsFound] = useState(false);
  const [isVinSelected, setIsVinSelected] = useState(false);
  const [vinPublicationNames, setVinPublicationNames] = useState([]);

  const [noModelResultsFound, setNoModelResultsFound] = useState(false);
  const [isModelSelected, setIsModelSelected] = useState(false);
  const [showCalendarModelError, setShowCalendarModelError] = useState(false);
  // Distinct fetch-failure state (kept separate from "no results" so the UI can
  // show an error + retry instead of a generic "not found").
  const [modelFetchError, setModelFetchError] = useState(false);
  // Debounce timer + abort controller for the model suggestion lookup.
  const modelDebounceTimerRef = useRef(null);
  const modelAbortRef = useRef(null);

  // Cancel any pending model lookup (debounce timer + in-flight request) on unmount.
  useEffect(() => {
    return () => {
      if (modelDebounceTimerRef.current) clearTimeout(modelDebounceTimerRef.current);
      if (modelAbortRef.current) modelAbortRef.current.abort();
    };
  }, []);

  // Publication tab: Model + Classification dependent autocompletes.
  // These reuse the parent's modelInput / classificationInput as the source of
  // truth (so the chat payload picks them up) but keep their own suggestion UI
  // state, independent of the Model tab's model autocomplete.
  const [pubModelSuggestions, setPubModelSuggestions] = useState([]);
  const [showPubModelSuggestions, setShowPubModelSuggestions] = useState(false);
  const [isLoadingPubModel, setIsLoadingPubModel] = useState(false);
  const [pubModelSelected, setPubModelSelected] = useState(false);
  const [noPubModelFound, setNoPubModelFound] = useState(false);
  const pubModelInputRef = useRef(null);
  const pubModelSuggestionsRef = useRef(null);
  const pubModelDebounceRef = useRef(null);
  const pubModelAbortRef = useRef(null);

  const [classSuggestions, setClassSuggestions] = useState([]);
  const [showClassSuggestions, setShowClassSuggestions] = useState(false);
  const [isLoadingClass, setIsLoadingClass] = useState(false);
  const [classSelected, setClassSelected] = useState(false);
  const [noClassFound, setNoClassFound] = useState(false);
  const classInputRef = useRef(null);
  const classSuggestionsRef = useRef(null);
  const classDebounceRef = useRef(null);
  const classAbortRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pubModelDebounceRef.current) clearTimeout(pubModelDebounceRef.current);
      if (pubModelAbortRef.current) pubModelAbortRef.current.abort();
      if (classDebounceRef.current) clearTimeout(classDebounceRef.current);
      if (classAbortRef.current) classAbortRef.current.abort();
    };
  }, []);

  // The Publication tab is valid only when BOTH a model and a classification
  // have been exactly selected (non-matching pairs are disallowed).
  useEffect(() => {
    if (searchType === "publication" && onValidationChange) {
      onValidationChange(!(pubModelSelected && classSelected));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pubModelSelected, classSelected, searchType]);

  const [
    isLoadingPublicationNumberSuggestions,
    setIsLoadingPublicationNumberSuggestions,
  ] = useState(false);
  const [noPublicationNumberResultsFound, setNoPublicationNumberResultsFound] =
    useState(false);
  const [isPublicationNumberSelected, setIsPublicationNumberSelected] =
    useState(false);

  // Publication number list for autocomplete
  const publicationNumberList = [
    "00ELT0211",
    "00ELT0212",
    "00ELT0213",
    "00ELT0266",
    "00ELT0267",
    "00ELT0268",
    "00ELT0269",
    "00ELT0270",
    "00ELT0312",
    "00ELT0313",
    "00ELT0314",
    "00ELT0315",
    "00ELT0316",
  ];

  // Publication number autocomplete state
  const [publicationNumberSuggestions, setPublicationNumberSuggestions] =
    useState([]);
  const [
    showPublicationNumberSuggestions,
    setShowPublicationNumberSuggestions,
  ] = useState(false);
  const publicationNumberInputRef = useRef(null);
  const publicationNumberSuggestionsRef = useRef(null);

  // Publication types for dropdown
  const publicationTypes = [
    { id: 1, name: "Chassis" },
    { id: 2, name: "Electrical" },
    { id: 3, name: "Electronic Control" },
    { id: 4, name: "Engine" },
    { id: 5, name: "General" },
  ];

  const [selectedPublication, setSelectedPublication] = useState(null);
  const [publicationDropdownOpen, setPublicationDropdownOpen] = useState(false);
  const [publicationModalOpen, setPublicationModalOpen] = useState(false);

  // Dynamic countries and dates from API
  const [dynamicCountriesJP, setDynamicCountriesJP] = useState([]);
  const [dynamicCountriesEN, setDynamicCountriesEN] = useState([]);
  const [dynamicMonthYearsJP, setDynamicMonthYearsJP] = useState([]);
  const [dynamicMonthYearsEN, setDynamicMonthYearsEN] = useState([]);
  const [dynamicPublicationsJP, setDynamicPublicationsJP] = useState([]);
  const [dynamicPublicationsEN, setDynamicPublicationsEN] = useState([]);
  const [modelDetailsData, setModelDetailsData] = useState(null);
  const [countryNameMapping, setCountryNameMapping] = useState({});

  // Vehicle type filter state
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState(null);
  const [vehicleTypeDropdownOpen, setVehicleTypeDropdownOpen] = useState(false);
  const [vehicleTypeSearch, setVehicleTypeSearch] = useState("");
  const [isLoadingVehicleTypes, setIsLoadingVehicleTypes] = useState(false);
  const vehicleTypeDropdownRef = useRef(null);
  const vehicleTypeSearchInputRef = useRef(null);

  // Search filters inside Country and Publication dropdowns
  const [countrySearch, setCountrySearch] = useState("");
  const [publicationSearch, setPublicationSearch] = useState("");
  const countrySearchInputRef = useRef(null);
  const publicationSearchInputRef = useRef(null);

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countryModalOpen, setCountryModalOpen] = useState(false);

  // Calendar states for month/year selection
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);

  // Months array for dropdown
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Generate array of years (current year and 10 years before)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 50 }, (_, i) => currentYear - i);

  // Refs for all dropdowns to detect clicks outside
  const publicationDropdownRef = useRef(null);
  const countryDropdownRef = useRef(null);
  const groupDropdownRef = useRef(null);
  const subGroupDropdownRef = useRef(null);

  // Fetch vehicle types from API (called once when model search tab is active)
  const fetchVehicleTypes = async () => {
    setIsLoadingVehicleTypes(true);
    try {
      const response = await fetch(CHAT_API_ENDPOINTS.SHOP_MANUAL_VEHICLE_TYPES);
      if (response.ok) {
        const data = await response.json();
        if (data.vehicle_types && Array.isArray(data.vehicle_types)) {
          setVehicleTypes(data.vehicle_types);
        }
      }
    } catch (error) {
      console.error("Error fetching vehicle types:", error);
    } finally {
      setIsLoadingVehicleTypes(false);
    }
  };

  // API call for VIN suggestions
  const fetchVinSuggestions = async (vinValue) => {
  // Trim spaces from the beginning and end of the VIN value
  const trimmedVinValue = vinValue ? vinValue.trim() : "";

  if (!trimmedVinValue || trimmedVinValue.length < 1) {
    setVinSuggestions([]);
    setShowVinSuggestions(false);
    setNoVinResultsFound(false);
    setIsVinSelected(false);

    // Always validate if no VIN is entered
    if (onValidationChange) {
      onValidationChange(true);
    }
    return;
  }

  setIsLoadingVinSuggestions(true);
  setNoVinResultsFound(false);

  try {
    // Use the trimmed VIN value for the API call
    const response = await fetch(CHAT_API_ENDPOINTS.SHOP_MANUAL_VIN_SEARCH, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        vin_keyword: trimmedVinValue, // Send trimmed value to API
      }),
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data = await response.json();

    // Check if the response indicates no VIN found (vin is null and publication_names is empty)
    const isNoVinFound = (
      data.vin === null &&
      (!data.publication_names || data.publication_names.length === 0)
    );

    // If no VIN found, show validation message
    if (isNoVinFound) {
      setVinPublicationNames([]);
      setVinSuggestions([]);
      setNoVinResultsFound(true);
      setShowVinSuggestions(false);
      setIsVinSelected(false);

      // No VIN found, validation fails
      if (onValidationChange) {
        onValidationChange(true);
      }
      return;
    }

    // Extract publication names if available
    if (data.publication_names && Array.isArray(data.publication_names)) {
      setVinPublicationNames(data.publication_names);
    } else {
      setVinPublicationNames([]);
    }

    // Handle the specific response structure with vin_list array
    let formattedData = [];
    if (data.vin_list && Array.isArray(data.vin_list)) {
      formattedData = data.vin_list;
    } else if (Array.isArray(data)) {
      formattedData = data;
    } else if (data.results && Array.isArray(data.results)) {
      formattedData = data.results;
    } else if (data.data && Array.isArray(data.data)) {
      formattedData = data.data;
    } else if (typeof data === "object" && data.vin) {
      // Only add to formattedData if there's actual VIN data
      formattedData = [data];
    }

    setVinSuggestions(formattedData);

    // Check if the trimmed VIN matches any suggestion
    const isVinMatching = formattedData.some((vin) => {
      const vinValueToCheck =
        typeof vin === "string"
          ? vin
          : vin.vinNumber ||
            vin.vin ||
            vin.chassis_no ||
            vin.chassis_number ||
            JSON.stringify(vin);

      // Compare with trimmed values for both sides
      return (
        vinValueToCheck.toLowerCase().trim() === trimmedVinValue.toLowerCase()
      );
    });

    // FIXED: Only show validation message when NO suggestions are returned
    if (formattedData.length === 0) {
      // API returned no results - show "not found" message
      setNoVinResultsFound(true);
      setShowVinSuggestions(false);
      setIsVinSelected(false);

      // No suggestions, validation fails
      if (onValidationChange) {
        onValidationChange(true);
      }
    } else {
      // API returned results - NEVER show validation message
      setNoVinResultsFound(false);
      setShowVinSuggestions(true);

      // Check for exact match but don't show validation message
      if (isVinMatching) {
        setIsVinSelected(true);
        if (onValidationChange) {
          onValidationChange(false); // Valid - exact match
        }
      } else {
        setIsVinSelected(false);
        if (onValidationChange) {
          onValidationChange(false); // Valid - has suggestions even if not exact match
        }
      }
    }
  } catch (error) {
    console.error("Error fetching VIN suggestions:", error);

    // Show error message only after API fails
    setNoVinResultsFound(true);
    setShowVinSuggestions(false);
    setIsVinSelected(false);

    // Error means no valid selection possible
    if (onValidationChange) {
      onValidationChange(true);
    }
  } finally {
    setIsLoadingVinSuggestions(false);
  }
};


  // Handle VIN input change with API call for suggestions
  const handleVinInputChangeWithSuggestions = (e) => {
  const value = e.target.value;

  // Call the original handler from props with the original value (keeping spaces for display)
  if (handleVinInputChange) {
    handleVinInputChange(e);
  }

  // Reset validation states but don't trigger validation error yet
  setIsVinSelected(false);
  setNoVinResultsFound(false);
  
  // Only set validation to true (valid) if input is empty - this prevents sending
  if (onValidationChange) {
    onValidationChange(value.trim().length === 0);
  }

  // Fetch suggestions from API (this will handle validation inside)
  fetchVinSuggestions(value);
};


  const fetchPublicationNumberSuggestions = async (publicationValue) => {
  // Trim spaces from the beginning and end of the publication number
  const trimmedPublicationValue = publicationValue ? publicationValue.trim() : "";

  if (!trimmedPublicationValue || trimmedPublicationValue.length < 1) {
    setPublicationNumberSuggestions([]);
    setShowPublicationNumberSuggestions(false);
    setNoPublicationNumberResultsFound(false);
    setIsPublicationNumberSelected(false);

    // Always validate if no publication number is entered
    if (onValidationChange) {
      onValidationChange(true);
    }
    return;
  }

  setIsLoadingPublicationNumberSuggestions(true);
  setNoPublicationNumberResultsFound(false);

  try {
    // Use an API endpoint for publication numbers with trimmed value
    const response = await fetch(
      CHAT_API_ENDPOINTS.SHOP_MANUAL_PUBLICATION_SEARCH,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          publication_number_keyword: trimmedPublicationValue,
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data = await response.json();

    // Handle the specific response structure with publication_number_list
    let formattedData = [];
    if (
      data.publication_number_list &&
      Array.isArray(data.publication_number_list)
    ) {
      formattedData = data.publication_number_list;
    } else if (
      data.publication_list &&
      Array.isArray(data.publication_list)
    ) {
      formattedData = data.publication_list;
    } else if (Array.isArray(data)) {
      formattedData = data;
    } else if (data.results && Array.isArray(data.results)) {
      formattedData = data.results;
    } else if (data.data && Array.isArray(data.data)) {
      formattedData = data.data;
    } else if (typeof data === "object") {
      formattedData = [data];
    }

    setPublicationNumberSuggestions(formattedData);

    // Check if the entered Publication Number matches any suggestion (using trimmed value)
    const isPublicationNumberMatching = formattedData.some((pubNumber) => {
      const pubNumberValue =
        typeof pubNumber === "string" ? pubNumber : String(pubNumber);
      return pubNumberValue.toLowerCase().trim() === trimmedPublicationValue.toLowerCase();
    });

    // FIXED: Only show validation message when NO suggestions are returned
    if (formattedData.length === 0) {
      // API returned no results - show "not found" message
      setNoPublicationNumberResultsFound(true);
      setShowPublicationNumberSuggestions(false);
      setIsPublicationNumberSelected(false);

      // No suggestions, validation fails
      if (onValidationChange) {
        onValidationChange(true);
      }
    } else {
      // API returned results - NEVER show validation message
      setNoPublicationNumberResultsFound(false);
      setShowPublicationNumberSuggestions(true);

      // Check for exact match but don't show validation message
      if (isPublicationNumberMatching) {
        setIsPublicationNumberSelected(true);
        if (onValidationChange) {
          onValidationChange(false); // Valid - exact match
        }
      } else {
        setIsPublicationNumberSelected(false);
        if (onValidationChange) {
          onValidationChange(true); // Invalid - has suggestions but not an exact match
        }
      }
    }
  } catch (error) {
    console.error("Error fetching Publication Number suggestions:", error);

    // Show error message only after API fails
    setNoPublicationNumberResultsFound(true);
    setShowPublicationNumberSuggestions(false);
    setIsPublicationNumberSelected(false);

    // Error means no valid selection possible
    if (onValidationChange) {
      onValidationChange(true);
    }
  } finally {
    setIsLoadingPublicationNumberSuggestions(false);
  }
};


  // ----- Publication tab: Model + Classification dependent autocompletes -----

  const extractList = (data, key) => {
    if (data && Array.isArray(data[key])) return data[key];
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    if (data && Array.isArray(data.data)) return data.data;
    return [];
  };

  // Fetch model suggestions, narrowed by the currently selected classification.
  const fetchPubModelSuggestions = async (modelValue) => {
    const q = modelValue ? modelValue.trim() : "";
    const selectedClass = (classificationInput || "").trim();

    if (!q) {
      setPubModelSuggestions([]);
      setShowPubModelSuggestions(false);
      setNoPubModelFound(false);
      setPubModelSelected(false);
      return;
    }

    setIsLoadingPubModel(true);
    setNoPubModelFound(false);
    if (pubModelAbortRef.current) pubModelAbortRef.current.abort();
    const controller = new AbortController();
    pubModelAbortRef.current = controller;

    try {
      const response = await fetch(CHAT_API_ENDPOINTS.SHOP_MANUAL_MODEL_SUGGESTION, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model_keyword: q,
          classification: selectedClass,
        }),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();
      const list = extractList(data, "model_list");
      setPubModelSuggestions(list);

      // Selection only happens via an explicit click (handlePubModelSuggestionClick).
      // Typing a value that happens to equal a real model (e.g. "FE" is itself a
      // model code) must not auto-hide the dropdown — the user may still be
      // narrowing toward FE101, FE110, etc.
      if (list.length === 0) {
        setNoPubModelFound(true);
        setShowPubModelSuggestions(false);
      } else {
        setNoPubModelFound(false);
        setShowPubModelSuggestions(true);
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Error fetching model suggestions:", error);
      setNoPubModelFound(true);
      setShowPubModelSuggestions(false);
    } finally {
      setIsLoadingPubModel(false);
    }
  };

  // Fetch classification suggestions, narrowed by the currently selected model.
  const fetchClassSuggestions = async (classValue) => {
    const q = classValue ? classValue.trim() : "";
    const selectedModel = (modelInput || "").trim();

    if (!q) {
      setClassSuggestions([]);
      setShowClassSuggestions(false);
      setNoClassFound(false);
      setClassSelected(false);
      return;
    }

    setIsLoadingClass(true);
    setNoClassFound(false);
    if (classAbortRef.current) classAbortRef.current.abort();
    const controller = new AbortController();
    classAbortRef.current = controller;

    try {
      const response = await fetch(
        CHAT_API_ENDPOINTS.SHOP_MANUAL_CLASSIFICATION_SUGGESTION,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            classification_keyword: q,
            model_keyword: selectedModel,
          }),
          signal: controller.signal,
        }
      );
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();
      const list = extractList(data, "classification_list");
      setClassSuggestions(list);

      // Selection only happens via an explicit click (handleClassSuggestionClick) —
      // see the matching comment in fetchPubModelSuggestions.
      if (list.length === 0) {
        setNoClassFound(true);
        setShowClassSuggestions(false);
      } else {
        setNoClassFound(false);
        setShowClassSuggestions(true);
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Error fetching classification suggestions:", error);
      setNoClassFound(true);
      setShowClassSuggestions(false);
    } finally {
      setIsLoadingClass(false);
    }
  };

  const handlePubModelChange = (e) => {
    const value = e.target.value;
    handleModelInputChange(e); // keep parent modelInput in sync (chat payload)
    setPubModelSelected(false);
    setNoPubModelFound(false);
    if (pubModelDebounceRef.current) clearTimeout(pubModelDebounceRef.current);
    pubModelDebounceRef.current = setTimeout(
      () => fetchPubModelSuggestions(value),
      DEFAULT_DEBOUNCE_MS
    );
  };

  const handleClassChange = (e) => {
    const value = e.target.value;
    handleClassificationInputChange(e); // keep parent classificationInput in sync
    setClassSelected(false);
    setNoClassFound(false);
    if (classDebounceRef.current) clearTimeout(classDebounceRef.current);
    classDebounceRef.current = setTimeout(
      () => fetchClassSuggestions(value),
      DEFAULT_DEBOUNCE_MS
    );
  };

  const handlePubModelSuggestionClick = (model) => {
    const v = typeof model === "string" ? model : String(model);
    handleModelInputChange({ target: { value: v } });
    setPubModelSelected(true);
    setShowPubModelSuggestions(false);
    setNoPubModelFound(false);
    if (pubModelInputRef.current) pubModelInputRef.current.focus();
  };

  const handleClassSuggestionClick = (cls) => {
    const v = typeof cls === "string" ? cls : String(cls);
    handleClassificationInputChange({ target: { value: v } });
    setClassSelected(true);
    setShowClassSuggestions(false);
    setNoClassFound(false);
    if (classInputRef.current) classInputRef.current.focus();
  };

  // Finalize selection on blur (tabbing/clicking away) when the typed value is
  // an exact match to a fetched suggestion — covers users who type the full
  // value without clicking the dropdown item. Suggestion items use onMouseDown
  // + preventDefault so a genuine click registers before this blur fires.
  const handlePubModelBlur = () => {
    const trimmed = (modelInput || "").trim();
    if (
      trimmed &&
      pubModelSuggestions.some((m) => String(m).toUpperCase() === trimmed.toUpperCase())
    ) {
      setPubModelSelected(true);
    }
    setShowPubModelSuggestions(false);
  };

  const handleClassBlur = () => {
    const trimmed = (classificationInput || "").trim();
    if (
      trimmed &&
      classSuggestions.some((c) => String(c).toUpperCase() === trimmed.toUpperCase())
    ) {
      setClassSelected(true);
    }
    setShowClassSuggestions(false);
  };

  // Handle VIN suggestion selection
  const handleVinSuggestionClick = (vin) => {
    // Handle string or object type for VIN
    const vinValue =
      typeof vin === "string"
        ? vin
        : vin.vinNumber ||
          vin.vin ||
          vin.chassis_no ||
          vin.chassis_number ||
          JSON.stringify(vin);

    // Trim the selected VIN value
    const trimmedVinValue = vinValue.trim();

    const event = {
      target: { value: trimmedVinValue }, // Use trimmed value
    };

    if (handleVinInputChange) {
      handleVinInputChange(event);
    }

    setShowVinSuggestions(false);
    setNoVinResultsFound(false);

    // Mark as selected since user picked from list
    setIsVinSelected(true);

    // Since user selected from list, validation passes
    if (onValidationChange) {
      onValidationChange(false);
    }

    // Focus back on input
    if (vinInputRef.current) {
      vinInputRef.current.focus();
    }
  };

  // API call for Model suggestions
  const fetchModelSuggestions = async (modelValue) => {
    const trimmedModelValue = modelValue ? modelValue.trim() : "";
    const requestId = ++modelSuggestionsRequestIdRef.current;
    latestModelQueryRef.current = trimmedModelValue;

    if (!trimmedModelValue || trimmedModelValue.length < 1) {
      setModelSuggestions([]);
      setShowModelSuggestions(false);
      setNoModelResultsFound(false);
      setModelFetchError(false);
      setIsModelSelected(false);

      // Always validate if no model is entered
      if (onValidationChange) {
        onValidationChange(true);
      }
      return;
    }

    setIsLoadingModelSuggestions(true);
    setNoModelResultsFound(false);
    setModelFetchError(false);

    // Abort any earlier in-flight lookup so only the latest query resolves.
    if (modelAbortRef.current) {
      modelAbortRef.current.abort();
    }
    const controller = new AbortController();
    modelAbortRef.current = controller;

    try {
      const response = await fetch(CHAT_API_ENDPOINTS.SHOP_MANUAL_MODEL_SUGGESTION, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model_keyword: trimmedModelValue,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();

      if (requestId !== modelSuggestionsRequestIdRef.current) {
        return;
      }

      // Handle the specific response structure
      let formattedData = [];
      if (data.model_list && Array.isArray(data.model_list)) {
        formattedData = data.model_list;
      } else if (Array.isArray(data)) {
        formattedData = data;
      } else if (data.results && Array.isArray(data.results)) {
        formattedData = data.results;
      } else if (data.data && Array.isArray(data.data)) {
        formattedData = data.data;
      } else if (typeof data === "object") {
        formattedData = [data];
      }

      setModelSuggestions(formattedData);

      // Check if the entered model matches any suggestion exactly
      const exactMatch = formattedData.find((model) => {
        const modelValueStr = typeof model === "string" ? model : String(model);
        return modelValueStr.toUpperCase() === trimmedModelValue.toUpperCase();
      });

      // Only show validation message when NO suggestions are returned
      if (formattedData.length === 0) {
        // API returned no results - show "not found" message
        setNoModelResultsFound(true);
        setShowModelSuggestions(false);
        setIsModelSelected(false);

        // No suggestions, validation fails
        if (onValidationChange) {
          onValidationChange(true);
        }
      } else {
        // API returned results - NEVER show validation message
        setNoModelResultsFound(false);

        // Always show suggestions when we have results
        setShowModelSuggestions(true);

        // Check for exact match
        if (exactMatch) {
          setIsModelSelected(true);
          if (onValidationChange) {
            onValidationChange(false); // Valid - exact match
          }

          // Reset dependent fields when a new model is typed
          setSelectedPublication(null);
          setSelectedCountry(null);
          setSelectedYear(null);

          // console.log("Exact model match found - Auto-fetching model details");

          // Automatically fetch model details for exact match
          await fetchModelDetails(trimmedModelValue, selectedVehicleType || "");
        } else {
          setIsModelSelected(false);
          if (onValidationChange) {
            onValidationChange(true); // Invalid - has suggestions but not an exact match
          }
        }
      }
    } catch (error) {
      // Superseded request aborted by a newer keystroke — ignore it.
      if (error.name === "AbortError") {
        return;
      }
      if (requestId !== modelSuggestionsRequestIdRef.current) {
        return;
      }

      console.error("Error fetching Model suggestions:", error);

      // Distinct fetch-failure state (kept separate from "no results") so the UI
      // can offer a retry instead of a generic "not found" message.
      setModelFetchError(true);
      setShowModelSuggestions(false);
      setIsModelSelected(false);

      // Error means no valid selection possible
      if (onValidationChange) {
        onValidationChange(true);
      }
    } finally {
      if (requestId === modelSuggestionsRequestIdRef.current) {
        setIsLoadingModelSuggestions(false);
      }
    }
  };

  // Handle model input change with autocomplete
  const handleModelInputChangeWithSuggestions = (e) => {
    const value = e.target.value;

    // Call the original handler
    handleModelInputChange(e);

    // Reset validation states but don't show error immediately
    setIsModelSelected(false);
    setNoModelResultsFound(false);
    setModelFetchError(false);
    setShowCalendarModelError(false);

    // Only set validation to true if input is empty
    if (onValidationChange) {
      onValidationChange(value.trim().length === 0);
    }

    // Debounced fetch — avoids a request per keystroke and, together with the
    // AbortController in fetchModelSuggestions, prevents stale-response races.
    if (modelDebounceTimerRef.current) {
      clearTimeout(modelDebounceTimerRef.current);
    }
    modelDebounceTimerRef.current = setTimeout(() => {
      fetchModelSuggestions(value);
    }, DEFAULT_DEBOUNCE_MS);
  };

  // Handle model input click to show suggestions
  const handleModelInputClick = () => {
    const currentValue = modelInput.trim();

    // If there's a value, fetch and show suggestions
    if (currentValue.length > 0) {
      // Show existing suggestions if available
      if (modelSuggestions.length > 0) {
        setShowModelSuggestions(true);
      } else {
        // Fetch suggestions from API
        fetchModelSuggestions(currentValue);
      }
    }
  };

  // Handle paste event for model input
  const handleModelPaste = async (e) => {
    // Get pasted text
    const pastedText = e.clipboardData.getData('text').trim();

    if (pastedText) {
      // Prevent default paste behavior
      e.preventDefault();

      // Set the input value
      const event = {
        target: { value: pastedText }
      };
      handleModelInputChange(event);

      // Reset validation states
      setIsModelSelected(false);
      setNoModelResultsFound(false);
      setShowCalendarModelError(false);

      // Fetch suggestions and check for match
      await fetchModelSuggestions(pastedText);

      // After fetching suggestions, check if there's an exact match
      setTimeout(async () => {
        const response = await fetch(CHAT_API_ENDPOINTS.SHOP_MANUAL_MODEL_SUGGESTION, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model_keyword: pastedText,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          let formattedData = [];
          if (data.model_list && Array.isArray(data.model_list)) {
            formattedData = data.model_list;
          }

          // Check for exact match
          const exactMatch = formattedData.find((model) => {
            const modelValue = typeof model === "string" ? model : String(model);
            return modelValue.toUpperCase() === pastedText.toUpperCase();
          });

          // If exact match found, fetch model details automatically
          if (exactMatch) {
            setIsModelSelected(true);
            setShowModelSuggestions(false);
            if (onValidationChange) {
              onValidationChange(false);
            }

            // Reset dependent fields
            setSelectedPublication(null);
            setSelectedCountry(null);
            setSelectedYear(null);

            // Fetch model details
            await fetchModelDetails(pastedText, selectedVehicleType || "");
          }
        }
      }, 100);
    }
  };


  // Function to fetch model details when a model and/or vehicle type is selected.
  // Single API call — response includes both country (EN) and country_ja (JP).
  const fetchModelDetails = async (modelKeyword, vehicleTypeKeyword = "") => {
    try {
      const trimmedModelKeyword = modelKeyword ? modelKeyword.trim() : "";
      const trimmedVehicleTypeKeyword = vehicleTypeKeyword ? vehicleTypeKeyword.trim() : "";

      const response = await fetch(CHAT_API_ENDPOINTS.SHOP_MANUAL_MODEL_DETAILS, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model_keyword: trimmedModelKeyword,
          vehicle_type_keyword: trimmedVehicleTypeKeyword,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch model details");
      }

      const data = await response.json();

      if (
        trimmedModelKeyword &&
        latestModelQueryRef.current &&
        latestModelQueryRef.current.toUpperCase() !== trimmedModelKeyword.toUpperCase()
      ) {
        return null;
      }

      // The tab may have changed while this request was in flight (e.g. the
      // user switched to the Model + Classification tab). This data is only
      // relevant to the Model tab's Country/Year pickers — applying it here
      // would leak a stale Country/Year into the other tab's state.
      if (searchTypeRef.current !== "model") {
        return null;
      }

      const detailsKey = `${trimmedModelKeyword}|${trimmedVehicleTypeKeyword}`;
      if (!lastModelDetailsKeyRef.current) {
        lastModelDetailsKeyRef.current = detailsKey;
      } else if (lastModelDetailsKeyRef.current !== detailsKey) {
        setSelectedPublication(null);
        setSelectedCountry(null);
        setSelectedYear(null);
        lastModelDetailsKeyRef.current = detailsKey;
      }

      setModelDetailsData(data);

      if (data && data.model_info && Array.isArray(data.model_info)) {
        // Deduplicate countries by English name (the stable key)
        const seenEN = new Set();
        const uniqueItems = data.model_info.filter(item => {
          if (seenEN.has(item.country)) return false;
          seenEN.add(item.country);
          return true;
        });

        // English country list
        const countriesEN = uniqueItems.map((item, index) => ({
          id: index + 1,
          name: item.country,
          code: item.country.substring(0, 2).toUpperCase(),
        }));
        setDynamicCountriesEN(countriesEN);

        // Japanese country list — fall back to English when country_ja is null
        const countriesJP = uniqueItems.map((item, index) => ({
          id: index + 1,
          name: item.country_ja || item.country,
          code: (item.country_ja || item.country).substring(0, 2).toUpperCase(),
        }));
        setDynamicCountriesJP(countriesJP);

        // Month/year — English (keyed by country EN)
        const monthYearsEN = data.model_info.map(item => ({
          month: Number.isFinite(item.publication_month) ? item.publication_month - 1 : null,
          year: item.publication_year,
          country: item.country,
        }));
        setDynamicMonthYearsEN(monthYearsEN);

        // Month/year — Japanese (keyed by country_ja, falling back to EN)
        const monthYearsJP = data.model_info.map(item => ({
          month: Number.isFinite(item.publication_month) ? item.publication_month - 1 : null,
          year: item.publication_year,
          country: item.country_ja || item.country,
        }));
        setDynamicMonthYearsJP(monthYearsJP);

        // Publications — English
        const publicationsEN = [];
        data.model_info.forEach(item => {
          if (item.publication_name && Array.isArray(item.publication_name)) {
            item.publication_name.forEach(pubName => {
              publicationsEN.push({ name: pubName, country: item.country, year: item.publication_year });
            });
          }
        });
        setDynamicPublicationsEN(publicationsEN);

        // Publications — Japanese
        const publicationsJP = [];
        data.model_info.forEach(item => {
          if (item.publication_name && Array.isArray(item.publication_name)) {
            item.publication_name.forEach(pubName => {
              publicationsJP.push({ name: pubName, country: item.country_ja || item.country, year: item.publication_year });
            });
          }
        });
        setDynamicPublicationsJP(publicationsJP);

        // Build EN <-> JP country name mapping from single response fields
        const mapping = {};
        data.model_info.forEach(item => {
          const enName = item.country;
          const jpName = item.country_ja || item.country;
          mapping[enName] = { jp: jpName, en: enName };
          if (item.country_ja) {
            mapping[item.country_ja] = { jp: jpName, en: enName };
          }
        });
        setCountryNameMapping(mapping);
      }

      return data;
    } catch (error) {
      console.error("Error fetching model details:", error);
      return null;
    }
  };

  // Handle model suggestion selection
  const handleModelSuggestionClick = async (model) => {
    const modelValue = typeof model === "string" ? model : String(model);
    const trimmedModelValue = modelValue.trim();
    const event = {
      target: { value: trimmedModelValue },
    };
    handleModelInputChange(event);
    setShowModelSuggestions(false);
    setModelSuggestions([]);

    latestModelQueryRef.current = trimmedModelValue;

    // Mark as selected since user picked from list
    setIsModelSelected(true);
    setNoModelResultsFound(false);
    setShowCalendarModelError(false);

    // Since user selected from list, validation passes
    if (onValidationChange) {
      onValidationChange(false);
    }

    // Reset previously selected values when a new model is selected
    setSelectedPublication(null);
    setSelectedCountry(null);
    setSelectedYear(null);

    // console.log("Model selected - Reset publication, country, and calendar values");

    // Call the API to fetch model details
    await fetchModelDetails(trimmedModelValue, selectedVehicleType || "");

    // Focus back on input
    if (modelInputRef.current) {
      modelInputRef.current.focus();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the publication dropdown
      if (
        publicationDropdownRef.current &&
        !publicationDropdownRef.current.contains(event.target) &&
        publicationDropdownOpen
      ) {
        setPublicationDropdownOpen(false);
        setPublicationSearch("");
      }

      // Check if click is outside the country dropdown
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target) &&
        countryDropdownOpen
      ) {
        setCountryDropdownOpen(false);
        setCountrySearch("");
      }

      // Check if click is outside the group dropdown
      if (
        groupDropdownRef.current &&
        !groupDropdownRef.current.contains(event.target) &&
        groupDropdownOpen
      ) {
        setGroupDropdownOpen(false);
      }

      // Check if click is outside the subGroup dropdown
      if (
        subGroupDropdownRef.current &&
        !subGroupDropdownRef.current.contains(event.target) &&
        subGroupDropdownOpen
      ) {
        setSubGroupDropdownOpen(false);
      }

      // Check if click is outside the model suggestions
      if (
        modelSuggestionsRef.current &&
        !modelSuggestionsRef.current.contains(event.target) &&
        modelInputRef.current &&
        !modelInputRef.current.contains(event.target) &&
        showModelSuggestions
      ) {
        setShowModelSuggestions(false);
      }

      // Check if click is outside the VIN suggestions
      if (
        vinSuggestionsRef.current &&
        !vinSuggestionsRef.current.contains(event.target) &&
        vinInputRef.current &&
        !vinInputRef.current.contains(event.target) &&
        showVinSuggestions
      ) {
        setShowVinSuggestions(false);
      }

      // Check if click is outside the publication number suggestions
      if (
        publicationNumberSuggestionsRef.current &&
        !publicationNumberSuggestionsRef.current.contains(event.target) &&
        publicationNumberInputRef.current &&
        !publicationNumberInputRef.current.contains(event.target) &&
        showPublicationNumberSuggestions
      ) {
        setShowPublicationNumberSuggestions(false);
      }

      // Check if click is outside the vehicle type dropdown
      if (
        vehicleTypeDropdownRef.current &&
        !vehicleTypeDropdownRef.current.contains(event.target) &&
        vehicleTypeDropdownOpen
      ) {
        setVehicleTypeDropdownOpen(false);
        setVehicleTypeSearch("");
      }

      // Publication tab: Model autocomplete
      if (
        pubModelSuggestionsRef.current &&
        !pubModelSuggestionsRef.current.contains(event.target) &&
        pubModelInputRef.current &&
        !pubModelInputRef.current.contains(event.target) &&
        showPubModelSuggestions
      ) {
        setShowPubModelSuggestions(false);
      }

      // Publication tab: Classification autocomplete
      if (
        classSuggestionsRef.current &&
        !classSuggestionsRef.current.contains(event.target) &&
        classInputRef.current &&
        !classInputRef.current.contains(event.target) &&
        showClassSuggestions
      ) {
        setShowClassSuggestions(false);
      }
    };

    // Add event listener when any dropdown is open
    if (
      publicationDropdownOpen ||
      countryDropdownOpen ||
      groupDropdownOpen ||
      subGroupDropdownOpen ||
      showModelSuggestions ||
      showVinSuggestions ||
      showPublicationNumberSuggestions ||
      showPubModelSuggestions ||
      showClassSuggestions ||
      vehicleTypeDropdownOpen
    ) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    publicationDropdownOpen,
    countryDropdownOpen,
    groupDropdownOpen,
    subGroupDropdownOpen,
    showModelSuggestions,
    showVinSuggestions,
    showPublicationNumberSuggestions,
    showPubModelSuggestions,
    showClassSuggestions,
    vehicleTypeDropdownOpen,
    setGroupDropdownOpen,
    setSubGroupDropdownOpen,
  ]);

  useEffect(() => {
    // Sync the local publicationNumberInput with the parent's publicationNumber state
    // This is needed when the component re-renders with preserved values
    if (window.searchParametersCallback) {
      const data = {
        selectedPublication,
        selectedCountry,
        selectedMonth,
        selectedYear,
        publicationNumberInput,
        selectedVehicleType,
      };
      window.searchParametersCallback(data);
    }
  }, [
    selectedPublication,
    selectedCountry,
    selectedMonth,
    selectedYear,
    publicationNumberInput,
    selectedVehicleType,
  ]);

  useEffect(() => {
    // When component mounts or cycleId changes, check if we should preserve values
    if (preserveValues && window.getSearchParametersCallback) {
      const parentData = window.getSearchParametersCallback();
      if (parentData) {
        if (parentData.publicationNumber && !publicationNumberInput) {
          setPublicationNumberInput(parentData.publicationNumber);
        }
        if (parentData.selectedPublication && !selectedPublication) {
          setSelectedPublication(parentData.selectedPublication);
        }
        if (parentData.selectedCountry && !selectedCountry) {
          setSelectedCountry(parentData.selectedCountry);
        }
        if (parentData.selectedMonth !== null && selectedMonth === null) {
          setSelectedMonth(parentData.selectedMonth);
        }
        if (parentData.selectedYear !== null && selectedYear === null) {
          setSelectedYear(parentData.selectedYear);
        }
      }
    }
  }, [cycleId, preserveValues]);

  useEffect(() => {
    if (preserveValues && (modelInput.trim() || selectedVehicleType) && !modelDetailsData) {
      fetchModelDetails(modelInput, selectedVehicleType || "");
    }
  }, [preserveValues, modelInput, modelDetailsData, selectedVehicleType]);

  useEffect(() => {
    if (preserveValues && vinInput.trim() && vinPublicationNames.length === 0) {
      fetchVinSuggestions(vinInput);
    }
  }, [preserveValues, vinInput, vinPublicationNames.length]);

  useEffect(() => {
    const previousModelInput = lastModelInputRef.current || "";
    const currentModelInput = modelInput || "";
    const currentTrimmed = currentModelInput.trim();
    const previousTrimmed = previousModelInput.trim();

    if (preserveValues && !previousModelInput) {
      lastModelInputRef.current = currentModelInput;
      return;
    }

    if (!currentTrimmed) {
      setSelectedPublication(null);
      setSelectedCountry(null);
      setSelectedYear(null);
      if (selectedVehicleType) {
        // Vehicle type still active — re-fetch with vehicle type only
        fetchModelDetails("", selectedVehicleType);
      } else {
        setDynamicCountriesJP([]);
        setDynamicCountriesEN([]);
        setDynamicMonthYearsJP([]);
        setDynamicMonthYearsEN([]);
        setDynamicPublicationsJP([]);
        setDynamicPublicationsEN([]);
        setModelDetailsData(null);
        setCountryNameMapping({});
      }
      lastModelInputRef.current = currentModelInput;
      return;
    }

    if (currentTrimmed !== previousTrimmed) {
      setSelectedPublication(null);
      setSelectedCountry(null);
      setSelectedYear(null);
    }

    lastModelInputRef.current = currentModelInput;
  }, [modelInput, preserveValues]);

  useEffect(() => {
    const previousVinInput = lastVinInputRef.current || "";
    const currentVinInput = vinInput || "";

    if (preserveValues && !previousVinInput) {
      lastVinInputRef.current = currentVinInput;
      return;
    }

    if (currentVinInput.trim() !== previousVinInput.trim()) {
      setSelectedPublication(null);
      setSelectedCountry(null);
      setSelectedYear(null);
    }

    lastVinInputRef.current = currentVinInput;
  }, [vinInput, preserveValues]);

  // Check if the current view is mobile on mount and when window resizes
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  useEffect(() => {
    // console.log(`Action triggered in cycle: ${cycleId}`);
    return () => {};
  }, [cycleId]);

  // Load vehicle types when model search tab becomes active
  useEffect(() => {
    if (searchType === "model" && vehicleTypes.length === 0) {
      fetchVehicleTypes();
    }
  }, [searchType]);

  // Get current language - computed value, no state needed
  const currentLanguage = t.i18n?.language || localStorage.getItem('selectedLanguage') || 'jp';

  // Get the appropriate country and month/year lists based on current language
  const dynamicCountries = currentLanguage === 'jp' ? dynamicCountriesJP : dynamicCountriesEN;
  const dynamicMonthYears = currentLanguage === 'jp' ? dynamicMonthYearsJP : dynamicMonthYearsEN;
  const dynamicPublications = currentLanguage === 'jp' ? dynamicPublicationsJP : dynamicPublicationsEN;

  // Form is ready for downstream selections when model or vehicle type is chosen
  const isSearchReady = isModelSelected || !!selectedVehicleType;

  // Mutual exclusion: each field is disabled when the other has a value
  const modelHasInput = modelInput.trim().length > 0;
  const vehicleTypeHasSelection = !!selectedVehicleType;
  const modelInputDisabled = vehicleTypeHasSelection;
  const vehicleTypeDisabled = modelHasInput;

  // Auto-select country when there's only one option (Model tab only — Country
  // isn't part of the Model + Classification tab's input at all)
  useEffect(() => {
    if (searchType !== "model") return;
    const countries = currentLanguage === 'jp' ? dynamicCountriesJP : dynamicCountriesEN;
    if (countries.length === 1 && !selectedCountry) {
      const country = countries[0];
      const enhancedCountry = {
        ...country,
        nameJP: countryNameMapping[country.name]?.jp || country.name,
        nameEN: countryNameMapping[country.name]?.en || country.name,
      };
      setSelectedCountry(enhancedCountry);
      if (onSelectionChange) {
        onSelectionChange();
      }
    }
  }, [searchType, dynamicCountriesJP, dynamicCountriesEN, currentLanguage, selectedCountry, countryNameMapping, onSelectionChange]);

  // Auto-select month when there's only one option (Model tab only)
  useEffect(() => {
    if (searchType !== "model") return;
    const monthYears = currentLanguage === 'jp' ? dynamicMonthYearsJP : dynamicMonthYearsEN;

    // Filter by selected country if one is selected
    let filteredData = monthYears;
    if (selectedCountry) {
      const countryNameToFilter = countryNameMapping[selectedCountry.name]?.[currentLanguage] || selectedCountry.name;
      filteredData = monthYears.filter(item => item.country === countryNameToFilter);
    }

    const uniqueMonths = [...new Set(filteredData.map(item => item.month))]
      .filter(Number.isFinite)
      .sort((a, b) => a - b);

    if (uniqueMonths.length === 1 && selectedMonth === null) {
      setSelectedMonth(uniqueMonths[0]);
      if (onSelectionChange) {
        onSelectionChange();
      }
    }
  }, [searchType, dynamicMonthYearsJP, dynamicMonthYearsEN, currentLanguage, selectedCountry, selectedMonth, countryNameMapping, onSelectionChange]);

  // Auto-select year when there's only one option (Model tab only)
  useEffect(() => {
    if (searchType !== "model") return;
    const monthYears = currentLanguage === 'jp' ? dynamicMonthYearsJP : dynamicMonthYearsEN;

    // Filter by selected country if one is selected
    let filteredData = monthYears;
    if (selectedCountry) {
      const countryNameToFilter = countryNameMapping[selectedCountry.name]?.[currentLanguage] || selectedCountry.name;
      filteredData = monthYears.filter(item => item.country === countryNameToFilter);
    }

    // Filter years based on selected month
    let uniqueYears;
    if (selectedMonth !== null) {
      const yearsForSelectedMonth = filteredData
        .filter(item => item.month === selectedMonth)
        .map(item => item.year);
      uniqueYears = [...new Set(yearsForSelectedMonth)].sort((a, b) => b - a);
    } else {
      uniqueYears = [...new Set(filteredData.map(item => item.year))].sort((a, b) => b - a);
    }

    if (uniqueYears.length === 1 && selectedYear === null) {
      setSelectedYear(uniqueYears[0]);
      if (onSelectionChange) {
        onSelectionChange();
      }
    }
  }, [searchType, dynamicMonthYearsJP, dynamicMonthYearsEN, currentLanguage, selectedCountry, selectedMonth, selectedYear, countryNameMapping, onSelectionChange]);


  const handleGroupClick = () => {
    if (isMobile) {
      setGroupModalOpen(true);
    } else {
      setGroupDropdownOpen(!groupDropdownOpen);
      // Close other dropdowns when opening this one
      setPublicationDropdownOpen(false);
      setCountryDropdownOpen(false);
      setSubGroupDropdownOpen(false);
    }
  };

  const handleSubGroupClick = () => {
    if (selectedGroup && !isLoadingSubGroups) {
      if (isMobile) {
        setSubGroupModalOpen(true);
      } else {
        setSubGroupDropdownOpen(!subGroupDropdownOpen);
        // Close other dropdowns when opening this one
        setPublicationDropdownOpen(false);
        setCountryDropdownOpen(false);
        setGroupDropdownOpen(false);
      }
    }
  };

  const handlePublicationClick = () => {
    if (isMobile) {
      setPublicationModalOpen(true);
    } else {
      setPublicationDropdownOpen(!publicationDropdownOpen);
      // Close other dropdowns when opening this one
      setCountryDropdownOpen(false);
      setGroupDropdownOpen(false);
      setSubGroupDropdownOpen(false);
    }
  };

  const handlePublicationSelect = (publication) => {
    setSelectedPublication(publication);
    setPublicationDropdownOpen(false);
    setPublicationModalOpen(false);
    setPublicationSearch("");
    if (onSelectionChange) {
      onSelectionChange();
    }
  };

  const handleCountryClick = () => {
    if (isMobile) {
      setCountryModalOpen(true);
    } else {
      setCountryDropdownOpen(!countryDropdownOpen);
      // Close other dropdowns when opening this one
      setPublicationDropdownOpen(false);
      setGroupDropdownOpen(false);
      setSubGroupDropdownOpen(false);
    }
  };

  const handleCountrySelect = (country) => {
    setCountrySearch("");
    // Enhanced country object with both JP and EN names
    const enhancedCountry = {
      ...country,
      nameJP: countryNameMapping[country.name]?.jp || country.name,
      nameEN: countryNameMapping[country.name]?.en || country.name,
    };

    const isDifferentCountry =
      selectedCountry?.name !== enhancedCountry.name ||
      selectedCountry?.nameEN !== enhancedCountry.nameEN ||
      selectedCountry?.nameJP !== enhancedCountry.nameJP;

    setSelectedCountry(enhancedCountry);
    setCountryDropdownOpen(false);
    setCountryModalOpen(false);

    if (isDifferentCountry) {
      setSelectedPublication(null);
      setSelectedMonth(null);
      setSelectedYear(null);
    }

    if (onSelectionChange) {
      onSelectionChange();
    }
  };

  const handleCalendarClick = () => {
    if (!modelDetailsData && !selectedVehicleType) {
      setShowCalendarModelError(true);
      return;
    }
    setShowCalendarModelError(false);
    setCalendarModalOpen(true);
  };

  const handleVehicleTypeClick = () => {
    setVehicleTypeDropdownOpen(!vehicleTypeDropdownOpen);
    setVehicleTypeSearch("");
    setCountryDropdownOpen(false);
    setPublicationDropdownOpen(false);
  };

  const handleVehicleTypeSelect = async (vehicleType) => {
    setSelectedVehicleType(vehicleType);
    setVehicleTypeDropdownOpen(false);
    setVehicleTypeSearch("");
    setSelectedCountry(null);
    setSelectedYear(null);
    setSelectedPublication(null);
    setShowCalendarModelError(false);

    if (onValidationChange) {
      onValidationChange(false);
    }

    await fetchModelDetails(isModelSelected ? modelInput.trim() : "", vehicleType);

    if (onSelectionChange) {
      onSelectionChange();
    }
  };

  const handleVehicleTypeClear = async (e) => {
    e.stopPropagation();
    setSelectedVehicleType(null);
    setVehicleTypeSearch("");
    setSelectedCountry(null);
    setSelectedYear(null);
    setSelectedPublication(null);

    if (onValidationChange) {
      onValidationChange(!isModelSelected);
    }

    if (isModelSelected && modelInput.trim()) {
      await fetchModelDetails(modelInput.trim(), "");
    } else {
      setDynamicCountriesJP([]);
      setDynamicCountriesEN([]);
      setDynamicMonthYearsJP([]);
      setDynamicMonthYearsEN([]);
      setDynamicPublicationsJP([]);
      setDynamicPublicationsEN([]);
      setModelDetailsData(null);
      setCountryNameMapping({});
    }

    if (onSelectionChange) {
      onSelectionChange();
    }
  };

  // Helper function to get country name in current language
  const getCountryNameInCurrentLanguage = (country) => {
    if (!country) return "";

    // If we have a mapping, use it
    if (countryNameMapping[country.name]) {
      return countryNameMapping[country.name][currentLanguage] || country.name;
    }

    // Otherwise return the original name
    return country.name;
  };

  // Get available months and years based on selected country and month
  const getAvailableMonthsYears = () => {
    if (dynamicMonthYears.length === 0) {
      return { availableMonths: null, availableYears: null };
    }

    const getCountryMatches = (list) => {
      if (!selectedCountry) {
        return list;
      }

      const countryNameToFilter =
        countryNameMapping[selectedCountry.name]?.[currentLanguage] || selectedCountry.name;
      const countryNamesToMatch = new Set(
        [
          countryNameToFilter,
          selectedCountry.name,
          selectedCountry.nameEN,
          selectedCountry.nameJP,
          selectedCountry.code,
        ].filter(Boolean)
      );

      return list.filter(item => countryNamesToMatch.has(item.country));
    };

    // Filter by selected country if one is selected
    let filteredData = getCountryMatches(dynamicMonthYears);

    if (selectedCountry && filteredData.length === 0) {
      const fallbackMonthYears =
        currentLanguage === "jp" ? dynamicMonthYearsEN : dynamicMonthYearsJP;
      filteredData = getCountryMatches(fallbackMonthYears);
    }

    // Extract unique months
    const uniqueMonths = [...new Set(filteredData.map(item => item.month))]
      .filter(Number.isFinite)
      .sort((a, b) => a - b);

    // Filter years based on selected month
    let uniqueYears;
    if (selectedMonth !== null && Number.isFinite(selectedMonth)) {
      // Only show years that are available for the selected month
      const yearsForSelectedMonth = filteredData
        .filter(item => item.month === selectedMonth)
        .map(item => item.year);
      uniqueYears = [...new Set(yearsForSelectedMonth)].sort((a, b) => b - a);
    } else {
      // If no month selected, show all available years
      uniqueYears = [...new Set(filteredData.map(item => item.year))].sort((a, b) => b - a);
    }

    return {
      availableMonths: uniqueMonths.length > 0 ? uniqueMonths : (selectedCountry ? [] : null),
      availableYears: uniqueYears.length > 0 ? uniqueYears : (selectedCountry ? [] : null),
    };
  };

  // Get available publications based on selected country and year
  const getAvailablePublications = () => {
    if (dynamicPublications.length === 0) {
      return [];
    }

    let filteredPublications = dynamicPublications;

    // Filter by selected country if one is selected
    if (selectedCountry) {
      const countryNameToFilter =
        countryNameMapping[selectedCountry.name]?.[currentLanguage] || selectedCountry.name;
      const countryNamesToMatch = new Set(
        [
          countryNameToFilter,
          selectedCountry.name,
          selectedCountry.nameEN,
          selectedCountry.nameJP,
          selectedCountry.code,
        ]
          .filter(Boolean)
      );

      filteredPublications = filteredPublications.filter(
        pub => countryNamesToMatch.has(pub.country)
      );
    }

    if (selectedCountry && filteredPublications.length === 0) {
      const fallbackPublications =
        currentLanguage === "jp" ? dynamicPublicationsEN : dynamicPublicationsJP;
      const countryNameToFilter =
        countryNameMapping[selectedCountry.name]?.[currentLanguage] || selectedCountry.name;
      const countryNamesToMatch = new Set(
        [
          countryNameToFilter,
          selectedCountry.name,
          selectedCountry.nameEN,
          selectedCountry.nameJP,
          selectedCountry.code,
        ]
          .filter(Boolean)
      );

      filteredPublications = fallbackPublications.filter(
        pub => countryNamesToMatch.has(pub.country)
      );
    }

    // Filter by selected year if one is selected
    if (selectedYear !== null) {
      filteredPublications = filteredPublications.filter(
        pub => pub.year === selectedYear
      );
    }

    // Get unique publication names
    const uniquePublicationNames = [...new Set(filteredPublications.map(pub => pub.name))];

    // Return as objects with id and name for consistency with existing structure
    return uniquePublicationNames.map((name, index) => ({
      id: index + 1,
      name: name,
    }));
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);

    // Clear selected year when month changes, as available years will be filtered
    // Only clear if the currently selected year is not available for this month
    if (selectedYear !== null) {
      // Filter by country first if selected
      let filteredData = dynamicMonthYears;
      if (selectedCountry) {
        const countryNameToFilter = countryNameMapping[selectedCountry.name]?.[currentLanguage] || selectedCountry.name;
        filteredData = dynamicMonthYears.filter(
          item => item.country === countryNameToFilter
        );
      }

      // Check if current year will be available after this month selection
      const yearsForNewMonth = filteredData
        .filter(item => item.month === month)
        .map(item => item.year);

      if (!yearsForNewMonth.includes(selectedYear)) {
        setSelectedYear(null);
      }
    }
  };

  const handleYearSelect = (year) => {
    setSelectedYear(year);
  };

  const handleCalendarClose = () => {
    setCalendarModalOpen(false);
    if (onSelectionChange) {
      onSelectionChange();
    }
  };

  // Handle publication number input change with autocomplete
  const handlePublicationNumberChangeWithSuggestions = (e) => {
  const value = e.target.value;
  setPublicationNumberInput(value);

  if (onSelectionChange) {
    onSelectionChange();
  }

  // Reset validation states but don't show error immediately
  setIsPublicationNumberSelected(false);
  setNoPublicationNumberResultsFound(false);

  // Set validation to invalid while typing - API will set to valid if exact match found
  if (onValidationChange) {
    onValidationChange(true);
  }

  // Fetch suggestions from API (this will handle validation inside)
  fetchPublicationNumberSuggestions(value);
};


  // Handle publication number suggestion selection
  const handlePublicationNumberSuggestionClick = (pubNumber) => {
    // The response contains simple strings, so we can use it directly
    // Trim the selected publication number to remove any spaces
    const trimmedPubNumber = pubNumber ? pubNumber.trim() : pubNumber;
    setPublicationNumberInput(trimmedPubNumber);
    setShowPublicationNumberSuggestions(false);
    setNoPublicationNumberResultsFound(false);

    // Mark as selected since user picked from list
    setIsPublicationNumberSelected(true);

    // Since user selected from list, validation passes
    if (onValidationChange) {
      onValidationChange(false);
    }

    // Focus back on input
    if (publicationNumberInputRef.current) {
      publicationNumberInputRef.current.focus();
    }
  };

  const resetAllLocalStates = (shouldPreserveValues = preserveValues) => {
    // If preserving values, get current values from parent before resetting
    let currentPublicationNumber = "";
    let currentSelectedPublication = null;
    let currentSelectedCountry = null;
    let currentSelectedMonth = null;
    let currentSelectedYear = null;

    if (shouldPreserveValues && window.getSearchParametersCallback) {
      const parentData = window.getSearchParametersCallback();
      if (parentData) {
        currentPublicationNumber = parentData.publicationNumber || "";
        currentSelectedPublication = parentData.selectedPublication;
        currentSelectedCountry = parentData.selectedCountry;
        currentSelectedMonth = parentData.selectedMonth;
        currentSelectedYear = parentData.selectedYear;
      }
    }

    // Only reset if not preserving values
    if (!shouldPreserveValues) {
      setPublicationNumberInput("");
      setSelectedPublication(null);
      setSelectedCountry(null);
      setSelectedMonth(null);
      setSelectedYear(null);
    } else {
      // Restore preserved values
      setPublicationNumberInput(currentPublicationNumber);
      setSelectedPublication(currentSelectedPublication);
      setSelectedCountry(currentSelectedCountry);
      setSelectedMonth(currentSelectedMonth);
      setSelectedYear(currentSelectedYear);
    }

    // Always reset modal states
    setPublicationDropdownOpen(false);
    setCountryDropdownOpen(false);
    setCalendarModalOpen(false);
    setGroupModalOpen(false);
    setSubGroupModalOpen(false);
    setPublicationModalOpen(false);
    setCountryModalOpen(false);

    // Reset suggestion states for VIN and Model
    setVinSuggestions([]);
    setShowVinSuggestions(false);
    setNoVinResultsFound(false);
    setIsVinSelected(false);
    setVinPublicationNames([]);
    setModelSuggestions([]);
    setShowModelSuggestions(false);
    setNoModelResultsFound(false);
    setIsModelSelected(false);
    setShowCalendarModelError(false);

    // Reset Publication tab Model + Classification autocomplete states
    setPubModelSuggestions([]);
    setShowPubModelSuggestions(false);
    setNoPubModelFound(false);
    setPubModelSelected(false);
    setClassSuggestions([]);
    setShowClassSuggestions(false);
    setNoClassFound(false);
    setClassSelected(false);

    // Reset dynamic model data when switching search types
    if (!shouldPreserveValues) {
      setDynamicCountriesJP([]);
      setDynamicCountriesEN([]);
      setDynamicMonthYearsJP([]);
      setDynamicMonthYearsEN([]);
      setDynamicPublicationsJP([]);
      setDynamicPublicationsEN([]);
      setModelDetailsData(null);
      setCountryNameMapping({});
    }

    // Only reset publication number suggestions if not preserving values OR if no publication number
    if (!shouldPreserveValues || !currentPublicationNumber.trim()) {
      setPublicationNumberSuggestions([]);
      setShowPublicationNumberSuggestions(false);
      setNoPublicationNumberResultsFound(false);
      setIsPublicationNumberSelected(false);
    }

    // Handle validation
    if (!shouldPreserveValues && onValidationChange) {
      onValidationChange(true);
    } else if (
      shouldPreserveValues &&
      currentPublicationNumber.trim() &&
      onValidationChange
    ) {
      // If preserving values and publication number exists, validate it
      setTimeout(() => {
        fetchPublicationNumberSuggestions(currentPublicationNumber);
      }, 100);
    }
  };

  const handleVinSearchClick = () => {
    setSearchType("vin");
    resetSearchState(false); // Don't preserve when switching search types
    resetAllLocalStates(false); // Don't preserve when switching search types
  };

  const handleModelSearchClick = () => {
    setSearchType("model");
    resetSearchState(false); // Don't preserve when switching search types
    resetAllLocalStates(false); // Don't preserve when switching search types
  };

  const handlePublicationSearchClick = () => {
    setSearchType("publication");
    resetSearchState(false); // Don't preserve when switching search types
    resetAllLocalStates(false); // Don't preserve when switching search types
  };

  return (
    <div key={instanceId} className="fusoChatFirst queryMarkPnc">
      <img src={fusoChat} alt="FUSO" className="w-6 h-6 fusoLogoSmall" />
      <div className="bg-red-50 p-4 pncChatFirst custom-radius">
        <div className="flex gap-3">
          <div className="flex-1">
            <p className="gap-[4px] searchHeadPnc">
              <span>{t("choosePara")}</span>
              <img
                className="targetIconPnc"
                src={searchPnc}
                alt="Search Icon"
              />
            </p>

            {/* Search Type Selector */}
            <div className="mt-4 space-y-4">
              <div className="flex gap-[16px] selectPartShop flex-wrap">
                <button
                  className={`px-4 py-2 rounded-lg w-full sm:flex-1 vinBtnPncNew vinBtnShopNew ${
                    searchType === "vin"
                      ? "bg-[#FF0000] text-white"
                      : "border border-red-500 hover:bg-[#FF0000] hover:text-white transition-colors duration-200"
                  }`}
                  style={
                    searchType === "vin"
                      ? { backgroundColor: "#FF0000", color: "white" }
                      : {}
                  }
                  onClick={handleVinSearchClick}
                >
                  {t("vinChassisSearch")}
                </button>
                <button
                  className={`px-4 py-2 rounded-lg w-full sm:flex-1 classificationPncNew modelShopNew ${
                    searchType === "model"
                      ? "bg-[#FF0000] text-white"
                      : "border border-red-500 hover:bg-[#FF0000] hover:text-white transition-colors duration-200"
                  }`}
                  style={
                    searchType === "model"
                      ? { backgroundColor: "#FF0000", color: "white" }
                      : {}
                  }
                  onClick={handleModelSearchClick}
                >
                  {t("modalSearchShop")}
                </button>

                <button
                  className={`px-4 py-2 rounded-lg w-full sm:flex-1 classificationPncNew modelShopNew ${
                    searchType === "publication"
                      ? "bg-[#FF0000] text-white"
                      : "border border-red-500 hover:bg-[#FF0000] hover:text-white transition-colors duration-200"
                  }`}
                  style={
                    searchType === "publication"
                      ? { backgroundColor: "#FF0000", color: "white" }
                      : {}
                  }
                  onClick={handlePublicationSearchClick}
                >
                  {t("modelClassification")}
                </button>
              </div>

              {/* VIN Input */}
              {searchType === "vin" && (
                <div className="grid gap-4">
                  <p className="flex vinBoxNew">{t("enterChassisMsg")}</p>
                  <div className="flex gap-[16px] vinSelectionShop flex-wrap">
                    <div className="relative flex-1">
                      <input
                        ref={vinInputRef}
                        type="text"
                        placeholder={t("enterChassisProMsg")}
                        value={vinInput}
                        onChange={handleVinInputChangeWithSuggestions}
                        className="rounded-lg px-4 py-2 vinInsertNew customInputShop w-full flex-wrap max-w-[342px]"
                        autoComplete="off"
                      />

                      {/* VIN Validation Message */}
                      {noVinResultsFound &&
  vinInput.trim().length >= 1 &&
  !isLoadingVinSuggestions && (
    <div className="absolute left-0 top-full text-red-500 text-sm mt-1 px-1 max-w-[340px] z-10">
      {t("noVinFound")}
    </div>
  )}

                      {/* Add this new validation message for when VIN doesn't match suggestions */}
                      {/* {!noVinResultsFound &&
                        vinSuggestions.length > 0 &&
                        vinInput.trim().length >= 1 &&
                        !isVinSelected &&
                        !isLoadingVinSuggestions && (
                          <div className="text-red-500 text-sm mt-1 px-1 max-w-[340px]">
                            {t("vinNotMatched")}
                          </div>
                        )} */}

                      {/* VIN suggestions dropdown - HIDDEN */}
                      {/* {showVinSuggestions && vinSuggestions.length > 0 && (
                        <div
                          ref={vinSuggestionsRef}
                          className="absolute mb-1 left-0 right-0 bottom-full mt-1 bg-white border border-gray-300 rounded-lg max-h-[10rem] overflow-y-auto z-30 shadow-md max-w-[342px]"
                        >
                          {isLoadingVinSuggestions ? (
                            <div className="px-4 py-2 text-gray-500">
                               {t('loading')}
                            </div>
                          ) : (
                            vinSuggestions.map((vin, index) => (
                              <div
                                key={`vin-suggestion-${index}`}
                                className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer border-b last:border-b-0"
                                onClick={() => handleVinSuggestionClick(vin)}
                              >
                                {typeof vin === "string" ? (
                                  <div className="font-medium">{vin}</div>
                                ) : (
                                  <>
                                    <div className="font-medium">
                                      {vin.vinNumber ||
                                        vin.vin ||
                                        vin.chassis_no ||
                                        vin.chassis_number ||
                                        JSON.stringify(vin)}
                                    </div>
                                    {(vin.model || vin.veh_model) && (
                                      <div className="text-sm text-gray-600">
                                        Model: {vin.model || vin.veh_model}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      )} */}
                    </div>
                  </div>
                </div>
              )}

              {/* Model/Classification Input */}
              {searchType === "model" && (
                <>
                  <p className="flex vinBoxNew">{t("enterModelMsg")}</p>

                  <div className="grid grid-cols-2 gap-4 vinSelectionShop customInputShop flex-wrap smallWrapShop">

                    {/* Vehicle Type — OR pair with Model, sits in col-1 row-1 */}
                    <div
                      ref={vehicleTypeDropdownRef}
                      className={`border border-gray-300 rounded-lg px-4 py-2 bg-white vinInsertNew flex dropBtnPnc relative ${vehicleTypeDisabled ? "opacity-40 cursor-not-allowed bg-gray-50" : ""}`}
                    >
                      <div
                        className={`w-full flex justify-between items-center gap-2 ${vehicleTypeDisabled ? "pointer-events-none" : "cursor-pointer"}`}
                        onClick={vehicleTypeDisabled ? undefined : handleVehicleTypeClick}
                      >
                        <span className={`text-sm truncate ${selectedVehicleType ? "text-black" : "text-gray-400"}`}>
                          {isLoadingVehicleTypes
                            ? (t("loading") || "Loading...")
                            : selectedVehicleType || "Vehicle Type"}
                        </span>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {selectedVehicleType && (
                            <span
                              role="button"
                              aria-label="Clear vehicle type"
                              onClick={handleVehicleTypeClear}
                              className="text-gray-400 hover:text-gray-700 text-base leading-none cursor-pointer px-0.5"
                            >
                              ×
                            </span>
                          )}
                          <img
                            src={dropPnc}
                            alt="Dropdown Icon"
                            className={`transition-transform duration-150 ${vehicleTypeDropdownOpen ? "rotate-180" : ""}`}
                          />
                        </div>
                      </div>

                      {/* Vehicle Type Desktop Dropdown */}
                      {!isMobile && vehicleTypeDropdownOpen && (
                        <div className="absolute left-0 bottom-full mb-1 bg-white border border-gray-300 rounded-lg w-full z-20 shadow-md dropdownStylePnc overflow-hidden">
                          <div className="max-h-32 overflow-y-auto">
                            {vehicleTypes.map((vt) => (
                              <div
                                key={vt}
                                className={`px-4 py-2 text-sm cursor-pointer hover:bg-[#FFE0E0] ${selectedVehicleType === vt ? "bg-[#FFE0E0] font-medium" : ""}`}
                                onClick={() => handleVehicleTypeSelect(vt)}
                              >
                                {vt}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Model Input — OR pair with Vehicle Type, sits in col-2 row-1 */}
                    <div className="relative">
                      <input
                        ref={modelInputRef}
                        type="text"
                        placeholder={t("enterModel")}
                        value={modelInput}
                        onChange={handleModelInputChangeWithSuggestions}
                        onPaste={handleModelPaste}
                        onClick={handleModelInputClick}
                        disabled={modelInputDisabled}
                        className={`border border-gray-300 rounded-lg px-4 py-2 vinInsertNew w-full customInputShop ${modelInputDisabled ? "opacity-40 cursor-not-allowed bg-gray-50" : ""}`}
                        autoComplete="off"
                      />

                      {noModelResultsFound &&
  modelInput.trim().length >= 1 && (
    <div className="absolute left-0 top-full text-red-500 text-sm mt-1 px-1 max-w-[340px] z-10">
      {t("noModelFound")}
    </div>
  )}

                      {!noModelResultsFound &&
                        modelSuggestions.length > 0 &&
                        modelInput.trim().length >= 1 &&
                        !isModelSelected &&
                        !showModelSuggestions && (
                          <div className="absolute left-0 top-full text-red-500 text-sm mt-1 px-1 max-w-[340px] z-10">
                            {t("modelNotMatched")}
                          </div>
                        )}

                      {/* Model suggestions dropdown */}
                      {showModelSuggestions && modelSuggestions.length > 0 && (
                        <div
                          ref={modelSuggestionsRef}
                          className="absolute left-0 right-0 bottom-full mt-1 bg-white border border-gray-300 rounded-lg max-h-40 overflow-y-auto z-20 shadow-md"
                        >
                          {isLoadingModelSuggestions ? (
                            <SuggestionSkeleton rows={4} />
                          ) : (
                            modelSuggestions.map((model, index) => (
                              <div
                                key={`model-suggestion-${index}`}
                                className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer border-b last:border-b-0"
                                onClick={() => handleModelSuggestionClick(model)}
                              >
                                {typeof model === "string" ? model : String(model)}
                              </div>
                            ))
                          )}
                        </div>
                      )}

                      {/* Loading / error state for model suggestions (shared search
                          kit). Shown when the main results dropdown is not — i.e. the
                          initial load (skeleton) or a fetch failure (error + retry). */}
                      {modelInput.trim().length >= 1 &&
                        !showModelSuggestions &&
                        (isLoadingModelSuggestions || modelFetchError) && (
                          <div className="absolute left-0 right-0 bottom-full mt-1 bg-white border border-gray-300 rounded-lg max-h-40 overflow-y-auto z-20 shadow-md">
                            {isLoadingModelSuggestions ? (
                              <SuggestionSkeleton rows={4} />
                            ) : (
                              <ErrorState
                                message={t("errorFindingData") || "Couldn't load suggestions"}
                                retryLabel={t("retry") || "Retry"}
                                onRetry={() => fetchModelSuggestions(modelInput)}
                              />
                            )}
                          </div>
                        )}
                    </div>

                    <div className={`${(noModelResultsFound && modelInput.trim().length >= 1) || (!noModelResultsFound && modelSuggestions.length > 0 && modelInput.trim().length >= 1 && !isModelSelected && !showModelSuggestions) ? 'mt-14 lg:mt-0' : ''}`}>
                      <div
                        ref={countryDropdownRef}
                        className="border border-gray-300 rounded-lg px-4 py-2 bg-white vinInsertNew flex dropBtnPnc relative"
                      >
                      <div
                        className="w-full cursor-pointer flex justify-between items-center whitespace-nowrap"
                        onClick={handleCountryClick}
                      >
                        <span>
                          {selectedCountry
                            ? getCountryNameInCurrentLanguage(selectedCountry)
                            : t("SelectsCountry")}
                        </span>
                        <img src={dropPnc} alt="Dropdown Icon" />
                      </div>

                      {/* Desktop Dropdown */}
                      {!isMobile && countryDropdownOpen && (
                        <div className="absolute left-0 bottom-full mb-1 bg-white border border-gray-300 rounded-lg w-full z-10 shadow-md dropdownStylePnc overflow-hidden">
                          {dynamicCountries.length > 0 && (
                            <div className="px-3 py-2 border-b border-gray-100">
                              <input
                                ref={countrySearchInputRef}
                                type="text"
                                placeholder={t("search") || "Search…"}
                                value={countrySearch}
                                onChange={(e) => setCountrySearch(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                className="w-full text-sm outline-none border border-gray-200 rounded px-2 py-1 focus:border-gray-400"
                                autoFocus
                              />
                            </div>
                          )}
                          <div className="max-h-[9rem] overflow-y-auto">
                            {dynamicCountries.length > 0 ? (
                              dynamicCountries
                                .filter((c) => c.name.toLowerCase().includes(countrySearch.toLowerCase()))
                                .map((country) => (
                                  <div
                                    key={`${cycleId}-${country.name}-${country.code}`}
                                    className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer"
                                    onClick={() => handleCountrySelect(country)}
                                  >
                                    {country.name}
                                  </div>
                                ))
                            ) : (
                              <div className="px-4 py-2 text-gray-500 text-center text-sm">
                                {isSearchReady ? t("loading") : (t("Please select a model or vehicle type first"))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      </div>
                    </div>

                    {/* Calendar Year Button */}
                    <div className={`relative ${(noModelResultsFound && modelInput.trim().length >= 1) || (!noModelResultsFound && modelSuggestions.length > 0 && modelInput.trim().length >= 1 && !isModelSelected && !showModelSuggestions) ? 'lg:mt-10' : ''}`}>
                      <div className="border border-gray-300 rounded-lg px-4 py-2 bg-white vinInsertNew flex dropBtnPnc relative">
                        <div
                          className="w-full cursor-pointer flex justify-between items-center whitespace-nowrap"
                          onClick={handleCalendarClick}
                        >
                          <span>
                            {selectedYear !== null
                              ? `${selectedYear}`
                              : t("Select Year")}
                          </span>
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                          </svg>
                        </div>
                      </div>

                      {/* Error message when calendar clicked without model or vehicle type */}
                      {showCalendarModelError && (
                        <div className="text-red-500 text-sm mt-1 px-1">
                          {t("Please select a model or vehicle type first")}
                        </div>
                      )}
                    </div>
                  </div>
                  {isLoadingModelClass && (
                    <p className="text-gray-500 text-sm mt-2"> {t("loading")} </p>
                  )}
                  {/* {!isLoadingModelClass && modelClassError && (
                    <p className="text-red-500 text-sm mt-2">
                      {modelClassError}
                    </p>
                  )} */}
                </>
              )}

              {/* Model + Classification selection (replaces publication number) */}
              {searchType === "publication" && (
                <div className="grid grid-cols-2 gap-4 vinSelectionShop customInputShop flex-wrap smallWrapShop">

                  {/* Model autocomplete (narrowed by selected classification) */}
                  <div className="relative">
                    <input
                      ref={pubModelInputRef}
                      type="text"
                      placeholder={t("enterModel")}
                      value={modelInput}
                      onChange={handlePubModelChange}
                      onClick={() => {
                        if (pubModelSuggestions.length > 0 && !pubModelSelected) {
                          setShowPubModelSuggestions(true);
                        }
                      }}
                      onBlur={handlePubModelBlur}
                      className="rounded-lg px-4 py-2 vinInsertNew customInputShop w-full"
                      autoComplete="off"
                    />

                    {noPubModelFound &&
                      modelInput.trim().length >= 1 &&
                      !isLoadingPubModel && (
                        <div className="text-red-500 text-sm mt-1 px-1 z-10">
                          {t("noModelFound")}
                        </div>
                      )}

                    {showPubModelSuggestions &&
                      pubModelSuggestions.length > 0 && (
                        <div
                          ref={pubModelSuggestionsRef}
                          className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-300 rounded-lg max-h-40 overflow-y-auto z-30 shadow-md"
                        >
                          {isLoadingPubModel ? (
                            <SuggestionSkeleton rows={4} />
                          ) : (
                            pubModelSuggestions.map((model, index) => (
                              <div
                                key={`pub-model-suggestion-${index}`}
                                className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer border-b last:border-b-0"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handlePubModelSuggestionClick(model)}
                              >
                                {typeof model === "string" ? model : String(model)}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                  </div>

                  {/* Classification autocomplete (narrowed by selected model) */}
                  <div className="relative">
                    <input
                      ref={classInputRef}
                      type="text"
                      placeholder={t("enterClassification")}
                      value={classificationInput}
                      onChange={handleClassChange}
                      onClick={() => {
                        if (classSuggestions.length > 0 && !classSelected) {
                          setShowClassSuggestions(true);
                        }
                      }}
                      onBlur={handleClassBlur}
                      className="rounded-lg px-4 py-2 vinInsertNew customInputShop w-full"
                      autoComplete="off"
                    />

                    {noClassFound &&
                      classificationInput.trim().length >= 1 &&
                      !isLoadingClass && (
                        <div className="text-red-500 text-sm mt-1 px-1 z-10">
                          {t("classificationNotFound")}
                        </div>
                      )}

                    {showClassSuggestions &&
                      classSuggestions.length > 0 && (
                        <div
                          ref={classSuggestionsRef}
                          className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-300 rounded-lg max-h-40 overflow-y-auto z-30 shadow-md"
                        >
                          {isLoadingClass ? (
                            <SuggestionSkeleton rows={4} />
                          ) : (
                            classSuggestions.map((cls, index) => (
                              <div
                                key={`classification-suggestion-${index}`}
                                className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer border-b last:border-b-0"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleClassSuggestionClick(cls)}
                              >
                                  {typeof cls === "string" ? cls : String(cls)}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Group Selection Modal */}
      {isMobile && groupModalOpen && (
        <div className="modal-mi fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-sm mx-4 custom-dropModal">
            <div className="bg-rose-50 flex justify-between items-center p-4 border-b custom-dropTop">
              <h3 className="text-lg font-medium">Select Group</h3>
              <button
                className="text-gray-500 hover:text-gray-700 crossFeedBtnPnc"
                onClick={() => setGroupModalOpen(false)}
              >
                <svg
                  className="svgFeedPnc"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {groups &&
                groups.map((group) => (
                  <div
                    key={`${cycleId}-${group.description}-${group.main_group}`}
                    className="p-4 hover:bg-[#FFE0E0] cursor-pointer border-b"
                    onClick={() => {
                      handleGroupSelect(
                        `${group.group_name}|${group.main_group}`
                      );
                      setGroupModalOpen(false);
                    }}
                  >
                    {group.main_group} - {group.group_name}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Sub-Group Selection Modal */}
      {isMobile && subGroupModalOpen && (
        <div className="modal-mi fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-sm mx-4 custom-dropModal">
            <div className="bg-rose-50 flex justify-between items-center p-4 border-b custom-dropTop">
              <h3 className="text-lg font-medium">Select Sub Group</h3>
              <button
                className="text-gray-500 hover:text-gray-700 crossFeedBtnPnc"
                onClick={() => setSubGroupModalOpen(false)}
              >
                <svg
                  className="svgFeedPnc"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {subGroups &&
                subGroups.map((subGroup) => (
                  <div
                    key={`${cycleId}-${subGroup.description}-${subGroup.sub_group}`}
                    className="p-4 hover:bg-[#FFE0E0] cursor-pointer border-b"
                    onClick={() => {
                      handleSubGroupSelect(
                        `${subGroup.sub_group}|${subGroup.group_name}`
                      );
                      setSubGroupModalOpen(false);
                    }}
                  >
                    {subGroup.sub_group} - {subGroup.group_name}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Calendar Selection Modal for both desktop and mobile */}
      {calendarModalOpen && (
        <div className="modal-mi fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-lg mx-4 custom-dropModal">
            <div className="bg-rose-50 flex justify-between items-center p-4 border-b custom-dropTop">
              <h3 className="text-lg font-medium">{t("Select Year")}</h3>
              <button
                className="text-gray-500 hover:text-gray-700 crossFeedBtnPnc"
                onClick={handleCalendarClose}
              >
                <svg
                  className="svgFeedPnc"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <label className="block text-lg font-medium text-gray-700 mb-3">
                  {t("Year")}
                </label>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3 max-h-60 overflow-y-auto">
                  {(() => {
                    const { availableYears } = getAvailableMonthsYears();
                    const yearsToShow = availableYears || years;
                    return yearsToShow.map((year) => {
                      const isAvailable = !availableYears || availableYears.includes(year);
                      return (
                        <div
                          key={`calendar-year-${year}`}
                          className={`py-3 text-center rounded-lg border ${
                            selectedYear === year
                              ? "bg-[#FFE0E0] text-red-500 border-red-500 font-medium"
                              : isAvailable
                              ? "hover:bg-slate-100 border-gray-300 cursor-pointer"
                              : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                          }`}
                          onClick={() => isAvailable && handleYearSelect(year)}
                        >
                          {year}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium text-lg"
                  onClick={handleCalendarClose}
                >
                  {t("Done")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Publication Selection Modal */}
      {isMobile && publicationModalOpen && (
        <div className="modal-mi fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-sm mx-4 custom-dropModal">
            <div className="bg-rose-50 flex justify-between items-center p-4 border-b custom-dropTop">
              <h3 className="text-lg font-medium">
                {t("Select Publication Type")}{" "}
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700 crossFeedBtnPnc"
                onClick={() => setPublicationModalOpen(false)}
              >
                <svg
                  className="svgFeedPnc"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto overflow-x-hidden">
              {(() => {
                // For model search, show dynamic publications from API
                // For VIN search, show VIN publication names from API
                let availablePublications = [];
                let emptyMessage = "";

                if (searchType === 'model') {
                  availablePublications = getAvailablePublications();
                  emptyMessage = t("Please select a model first");
                } else if (searchType === 'vin') {
                  availablePublications = vinPublicationNames.map((name, index) => ({ id: index + 1, name }));
                  emptyMessage = t("Please enter VIN first");
                }

                return availablePublications.length > 0 ? (
                  availablePublications.map((pub) => (
                    <div
                      key={`${cycleId}-${pub.name}-${pub.id}`}
                      className="p-4 hover:bg-[#FFE0E0] cursor-pointer border-b break-words"
                      onClick={() => handlePublicationSelect(pub)}
                    >
                      {pub.name}
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-gray-500 text-center">
                    {emptyMessage}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Country Selection Modal */}
      {isMobile && countryModalOpen && (
        <div className="modal-mi fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-sm mx-4 custom-dropModal">
            <div className="bg-rose-50 flex justify-between items-center p-4 border-b custom-dropTop">
              <h3 className="text-lg font-medium"> {t('SelectCountry')}</h3>
              <button
                className="text-gray-500 hover:text-gray-700 crossFeedBtnPnc"
                onClick={() => setCountryModalOpen(false)}
              >
                <svg
                  className="svgFeedPnc"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {dynamicCountries.length > 0 ? (
                dynamicCountries.map((country) => (
                  <div
                    key={`${cycleId}-${country.name}-${country.code}`}
                    className="p-4 hover:bg-[#FFE0E0] cursor-pointer border-b"
                    onClick={() => handleCountrySelect(country)}
                  >
                    {country.name}
                  </div>
                ))
              ) : (
                <div className="p-4 text-gray-500 text-center">
                  {t("Please select a model first")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchParameters;
